import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { ReactNode, useEffect, useMemo } from 'react'

import { useCowAnalytics } from '@cowprotocol/analytics'
import { useFeatureFlags } from '@cowprotocol/common-hooks'
import { getAddress } from '@cowprotocol/common-utils'
import { renderTooltip } from '@cowprotocol/ui'
import { isSafeViaWcAtom, isSafeWalletAtom, useWalletInfo } from '@cowprotocol/wallet'
import { TradeType } from '@cowprotocol/widget-lib'

import { useAdvancedOrdersDerivedState } from 'modules/advancedOrders'
import { AffiliateTraderRewardsRow, useIsRewardsRowEnabled } from 'modules/affiliate'
import { TradeApproveWithAffectedOrderList } from 'modules/erc20Approve'
import { useInjectedWidgetDeadline } from 'modules/injectedWidget'
import { TradeRows, useGetReceiveAmountInfo } from 'modules/trade'
import { useTradeState } from 'modules/trade/hooks/useTradeState'
import { TradeNumberInput } from 'modules/trade/pure/TradeNumberInput'
import { TradeTextBox } from 'modules/trade/pure/TradeTextBox'
import {
  useGetTradeFormValidations,
  useIsTradeFormValidationPassed,
  useShouldHideTradeRateDetails,
} from 'modules/tradeFormValidation'
import { TwapFormState } from 'modules/twap/pure/PrimaryActionButton/getTwapFormState'
import { TwapWorstAcceptedPriceInput } from 'modules/twap/pure/TwapWorstAcceptedPriceInput/TwapWorstAcceptedPriceInput.pure'

import { CowSwapAnalyticsCategory } from 'common/analytics/types'
import { usePrice } from 'common/hooks/usePrice'
import { useRateInfoParams } from 'common/hooks/useRateInfoParams'
import { RateInfo } from 'common/pure/RateInfo'

import * as styledEl from './styled'
import { useLabelsTooltips } from './tooltips'

import { DEFAULT_NUM_OF_PARTS, MAX_PART_TIME, MINIMUM_PART_TIME, ORDER_DEADLINES } from '../../const'
import {
  useFallbackHandlerVerification,
  useIsFallbackHandlerCompatible,
  useIsFallbackHandlerRequired,
} from '../../hooks/useFallbackHandlerVerification'
import { useTwapDemandAnalytics } from '../../hooks/useTwapDemandAnalytics'
import { useTwapFormState } from '../../hooks/useTwapFormState'
import { useTwapSlippage } from '../../hooks/useTwapSlippage'
import { DeadlineSelector } from '../../pure/DeadlineSelector/DeadlineSelector.pure'
import { TotalDurationTooltip } from '../../pure/TotalDurationTooltip/TotalDurationTooltip.pure'
import { twapRateInvertedAtom, twapTimeIntervalAtom } from '../../state/twapOrderAtom'
import { twapOrdersSettingsAtom, updateTwapOrdersSettingsAtom } from '../../state/twapOrdersSettingsAtom'
import { deadlinePartsDisplay } from '../../utils/deadlinePartsDisplay'
import { ActionButtons } from '../ActionButtons'
import { AmountParts } from '../AmountParts'
import { TwapFormWarnings } from '../TwapFormWarnings/TwapFormWarnings.container'

export type { LabelTooltip, LabelTooltipContent, LabelTooltipFn, LabelTooltipItems } from './tooltips'
export type { TotalDurationTooltipProps } from '../../pure/TotalDurationTooltip/TotalDurationTooltip.pure'
export { TotalDurationTooltip } from '../../pure/TotalDurationTooltip/TotalDurationTooltip.pure'

interface TwapFormWidget {
  tradeWarnings: ReactNode
  captcha: ReactNode | null
}

// TODO: Break down this large function into smaller functions
// eslint-disable-next-line max-lines-per-function
export function TwapFormWidget({ tradeWarnings, captcha }: TwapFormWidget): ReactNode {
  const { account } = useWalletInfo()
  const isSafeWallet = useAtomValue(isSafeWalletAtom)
  const isSafeViaWc = useAtomValue(isSafeViaWcAtom) === true
  const { isTwapEoaEnabled } = useFeatureFlags()
  const isEoaTwap = !!isTwapEoaEnabled && !isSafeWallet && !isSafeViaWc
  const isRewardsRowEnabled = useIsRewardsRowEnabled()

  const { numberOfPartsValue, deadline, customDeadline, isCustomDeadline } = useAtomValue(twapOrdersSettingsAtom)

  const { inputCurrency, outputCurrency, inputCurrencyAmount, outputCurrencyAmount } = useAdvancedOrdersDerivedState()
  const { updateState } = useTradeState()
  const isFallbackHandlerRequired = useIsFallbackHandlerRequired()
  const isFallbackHandlerCompatible = useIsFallbackHandlerCompatible()
  const verification = useFallbackHandlerVerification()

  const twapOrderSlippage = useTwapSlippage()
  const timeInterval = useAtomValue(twapTimeIntervalAtom)
  const updateSettingsState = useSetAtom(updateTwapOrdersSettingsAtom)

  const localFormValidation = useTwapFormState()
  const validations = useGetTradeFormValidations()
  const primaryFormValidation = validations?.[0] || null
  const isPrimaryValidationPassed = useIsTradeFormValidationPassed()

  const hideQuoteAmount = useShouldHideTradeRateDetails({ hideIfWrapUnwrap: true })
  const rateInfoParams = useRateInfoParams(inputCurrencyAmount, outputCurrencyAmount)

  const receiveAmountInfo = useGetReceiveAmountInfo()

  const executionPrice = usePrice(
    receiveAmountInfo?.amountsToSign.sellAmount,
    receiveAmountInfo?.amountsToSign.buyAmount,
  )

  const widgetDeadline = useInjectedWidgetDeadline(TradeType.ADVANCED)

  const cowAnalytics = useCowAnalytics()
  const { trackTwapTabOpened } = useTwapDemandAnalytics()

  useEffect(() => {
    if (widgetDeadline) {
      // Ensure min part duration
      const minDuration = Math.floor(MINIMUM_PART_TIME / 60) * 2 // it must have at least 2 parts

      const maxDuration = Math.floor(MAX_PART_TIME / 60) * numberOfPartsValue

      let minutes = widgetDeadline
      if (widgetDeadline < minDuration) {
        minutes = minDuration
      } else if (widgetDeadline > maxDuration) {
        minutes = maxDuration
      }

      updateSettingsState({
        customDeadline: { hours: 0, minutes },
        isCustomDeadline: true,
      })
    }
  }, [widgetDeadline, updateSettingsState, numberOfPartsValue])

  const isDeadlineDisabled = !!widgetDeadline

  const deadlineState = {
    deadline,
    customDeadline,
    isCustomDeadline,
  }

  // Reset warnings flags once on start
  useEffect(() => {
    updateSettingsState({ isFallbackHandlerSetupAccepted: false })
    cowAnalytics.sendEvent({
      category: CowSwapAnalyticsCategory.TWAP,
      action: 'Open Advanced Orders Tab',
    })
  }, [updateSettingsState, cowAnalytics])

  useEffect(() => {
    trackTwapTabOpened()
  }, [trackTwapTabOpened])

  useEffect(() => {
    if (account && verification) {
      if (localFormValidation === TwapFormState.WALLET_NOT_SUPPORTED) {
        cowAnalytics.sendEvent({
          category: CowSwapAnalyticsCategory.TWAP,
          action: 'non-compatible',
        })
      } else if (isFallbackHandlerRequired) {
        cowAnalytics.sendEvent({
          category: CowSwapAnalyticsCategory.TWAP,
          action: 'safe-that-could-be-converted',
        })
      } else if (isFallbackHandlerCompatible) {
        cowAnalytics.sendEvent({
          category: CowSwapAnalyticsCategory.TWAP,
          action: 'compatible',
        })
      }
    }
  }, [account, isFallbackHandlerRequired, isFallbackHandlerCompatible, localFormValidation, verification, cowAnalytics])

  const isInvertedState = useAtom(twapRateInvertedAtom)
  const ratePairKey = [inputCurrency?.chainId, getAddress(inputCurrency), getAddress(outputCurrency)].join(':')
  const [isInverted] = isInvertedState

  const { onSlippageInput, onNumOfPartsInput } = useMemo(() => {
    return {
      onSlippageInput: (value: number | null) => updateSettingsState({ slippageValue: value }),
      onNumOfPartsInput: (value: number | null) => {
        updateSettingsState({ numberOfPartsValue: value || DEFAULT_NUM_OF_PARTS })
        updateState?.({ outputCurrencyAmount: null })
      },
    }
  }, [updateSettingsState, updateState])

  const tooltips = useLabelsTooltips()

  return (
    <>
      {!hideQuoteAmount && (
        <TradeRows>
          <RateInfo
            key={ratePairKey}
            setSmartQuoteSelectionOnce
            label={tooltips.price.label}
            rateInfoParams={rateInfoParams}
            isInvertedState={isInvertedState}
            fontSize={13}
            rightAlign
          />
          {isRewardsRowEnabled && <AffiliateTraderRewardsRow />}
        </TradeRows>
      )}

      <TwapWorstAcceptedPriceInput
        slippageLabel={tooltips.slippage.label}
        slippageTooltip={renderTooltip(tooltips.slippage.tooltip)}
        twapOrderSlippage={twapOrderSlippage}
        onSlippageInput={onSlippageInput}
        executionPrice={executionPrice}
        inputCurrency={inputCurrency}
        outputCurrency={outputCurrency}
        hideQuoteAmount={hideQuoteAmount}
        isInverted={isInverted}
      />

      <styledEl.Row>
        <TradeNumberInput
          value={numberOfPartsValue}
          onUserInput={onNumOfPartsInput}
          min={DEFAULT_NUM_OF_PARTS}
          label={tooltips.numberOfParts.label}
          tooltip={renderTooltip(tooltips.numberOfParts.tooltip)}
          showUpDownArrows={true}
        />
      </styledEl.Row>

      <styledEl.Row>
        <DeadlineSelector
          deadline={deadlineState}
          isDeadlineDisabled={isDeadlineDisabled}
          items={ORDER_DEADLINES}
          setDeadline={updateSettingsState}
          label={tooltips.totalDuration.label}
          tooltip={TotalDurationTooltip}
          parts={numberOfPartsValue}
          partDuration={timeInterval}
        />

        <TradeTextBox label={tooltips.partDuration.label} tooltip={tooltips.partDuration.tooltip}>
          <>{deadlinePartsDisplay(timeInterval)}</>
        </TradeTextBox>
      </styledEl.Row>

      <AmountParts />

      {/* Local validation replaces the trade button with a disabled one, so trade hints and approval controls
          are pointless: only the warning explaining the block stays visible */}
      {!localFormValidation && tradeWarnings}
      <TwapFormWarnings localFormValidation={localFormValidation} />

      <TradeRows>
        {isPrimaryValidationPassed && !localFormValidation && (
          <TradeApproveWithAffectedOrderList approvalTarget={isEoaTwap ? 'poller' : undefined} />
        )}
      </TradeRows>

      {captcha}
      <ActionButtons
        fallbackHandlerIsNotSet={isFallbackHandlerRequired}
        localFormValidation={localFormValidation}
        primaryFormValidation={primaryFormValidation}
      />
    </>
  )
}
